import { TextField } from '@mui/material';
import React from 'react';
import moment from 'moment-timezone';
import { Field, Form, FormRenderProps } from 'react-final-form';
import FontAwesome from 'react-fontawesome';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useDispatch, useSelector } from 'react-redux';
import { FormApi } from 'final-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { AMAZON_BASE_URL, MENUS, required } from './Constants';
import TitleSection from './TitleSection';
import { reserveTable } from './actions/Actions';
import { AppStore, Store } from './types';

type FormValues = {
    email: string;
    date: moment.Moment;
    numberOfPersons: number;
    name: string;
    comment: string;
    phoneNumber: string;
};

const WORKING_DAYS = [0, 2, 3, 4, 5, 6];

const shouldDisableDate = (closures: AppStore['closures'], d: moment.Moment) =>
    !WORKING_DAYS.includes(d.day()) ||
    closures.some(({ startDate, endDate }) =>
        d.isBetween(startDate, endDate, 'day', '[]')
    );

const FormDateTimePicker = ({ input }) => {
    const closures = useSelector(({ app }: Store) => app.closures);

    const handleDate = React.useCallback(
        (d: moment.Moment) => shouldDisableDate(closures, d),
        [closures]
    );

    return (
        <DateTimePicker
            {...input}
            inputFormat='DD.MM.yyyy HH:mm'
            disablePast
            showTodayButton
            disableMaskedInput
            shouldDisableDate={handleDate}
            // eslint-disable-next-line react/jsx-no-bind
            renderInput={params => (
                <TextField {...params} className='date-picker field-input' />
            )}
        />
    );
};

const Reservation: React.FC = () => {
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const closures = useSelector(({ app }: Store) => app.closures);
    const storeConfiguration = useSelector(({ app }: Store) => app.storeConfiguration);

    const renderReservationForm = React.useCallback(
        ({ handleSubmit, validating, submitting }: FormRenderProps) => (
            <form className='form reservation-form' onSubmit={handleSubmit}>
                <div className='one-line'>
                    <Field name='email' validate={required}>
                        {({ input, meta }) => (
                            <div className='field-input wide'>
                                <input {...input} placeholder='Email' type='email' />
                                {meta.touched && meta.error && (
                                    <FontAwesome
                                        name='exclamation-circle'
                                        title={meta.error}
                                        className='error'
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                    <Field name='numberOfPersons' validate={required}>
                        {({ input, meta }) => (
                            <div className='field-input'>
                                <input {...input} type='number' min={1} max={40} />
                                {meta.touched && meta.error && (
                                    <FontAwesome
                                        name='exclamation-circle'
                                        title={meta.error}
                                        className='error'
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                    <Field name='date' component={FormDateTimePicker} />
                </div>
                <div className='one-line'>
                    <Field name='name' validate={required}>
                        {({ input, meta }) => (
                            <div className='field-input wide'>
                                <input {...input} placeholder='Naam' />
                                {meta.touched && meta.error && (
                                    <FontAwesome
                                        name='exclamation-circle'
                                        title={meta.error}
                                        className='error'
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                    <Field name='phoneNumber' validate={required}>
                        {({ input, meta }) => (
                            <div className='field-input wide'>
                                <input
                                    {...input}
                                    placeholder='Telefoonnummer'
                                    type='tel'
                                />
                                {meta.touched && meta.error && (
                                    <FontAwesome
                                        name='exclamation-circle'
                                        title={meta.error}
                                        className='error'
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                </div>
                <div className='one-line'>
                    <Field name='comment'>
                        {({ input, meta }) => (
                            <div className='field-input comment'>
                                <input {...input} placeholder='Opmerking' />
                                {meta.touched && meta.error && (
                                    <FontAwesome
                                        name='exclamation-circle'
                                        title={meta.error}
                                        className='error'
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                </div>
                <button
                    type='submit'
                    className='reserve'
                    disabled={validating || submitting}>
                    RESERVEREN
                </button>
                <div className='note'>
                    LET OP: Controleer uw e-mail en verifieer uw reservering om
                    veiligheidsredenen.
                </div>
                <div className='note'>
                    ATTENTION: Please check your email and verify your reservation for
                    security purposes.
                </div>
            </form>
        ),
        []
    );

    const handleReservation = React.useCallback(
        (values: FormValues, form: FormApi) =>
            dispatch(
                reserveTable({
                    ...values,
                    numberOfPersons: Number(values.numberOfPersons),
                    date: `${values.date.format('YYYY-MM-DDT')}${values.date.format(
                        'HH:mm'
                    )}Z`
                })
            ).then(() => {
                form.reset({
                    numberOfPersons: 1,
                    date: moment()
                });

                ['name', 'email', 'numberOfPersons', 'phoneNumber'].forEach(key => {
                    form.resetFieldState(key);
                });
            }),
        [dispatch]
    );

    if (!storeConfiguration) {
        return null;
    }

    return (
        <div
            className='reservation-section'
            id={MENUS.RESERVATION.hash}
            style={{
                backgroundImage: `url("${AMAZON_BASE_URL}/store___${storeConfiguration.shortUniqueID}/template/reservation_bg.jpg")`
            }}>
            <div className='container'>
                <TitleSection title='Tafel Reserveren' />
                <div className='reservation-info'>
                    <div className='opening-hours-label'>Openingstijden</div>
                    <br />
                    <div className='opening-hours'>
                        <div
                            style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                            Openingstijden Dinner
                        </div>
                        <div>Maadag: gesloten</div>
                        <div>Dinsdag: gesloten</div>
                        <div>Woendag: 16:30 uur - 22:00</div>
                        <div>Donderdag: 16:30 uur - 22:00</div>
                        <div>Vrijdag: 16:30 uur - 22:00</div>
                        <div>Zaterdag: 16:30 uur - 22:00</div>
                        <div>Zondag: 16:30 uur - 21:00</div>
                    </div>
                    <div style={{ textDecoration: 'underline' }}>
                        Bij ons kunt u ook eten afhalen
                    </div>
                    <div className='phone-numbers'>
                        <FontAwesome name='phone' className='phone' />
                        <a
                            href={`tel:${storeConfiguration.reservationsPhone1
                                ?.trim()
                                .replace(/\s/g, '')}`}>
                            {storeConfiguration.reservationsPhone1?.trim()}
                        </a>
                    </div>
                </div>
                {storeConfiguration.supportsReservationsFromWebsite ? (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Form
                            render={renderReservationForm}
                            onSubmit={handleReservation}
                            initialValues={{
                                numberOfPersons: 1,
                                date: shouldDisableDate(closures, moment())
                                    ? moment().add(1, 'day')
                                    : moment()
                            }}
                        />
                    </LocalizationProvider>
                ) : null}
            </div>
        </div>
    );
};

export default Reservation;
